import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal, Field } from '../ui/Modal';
import { Icon } from '../ui/Icon';
import { api, ApiError } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { CandidateStage } from '@masamune/shared';

const STAGE_LABEL: Record<string, string> = {
  NUOVO_CANDIDATO: 'Nuovo candidato', SCREENING: 'Screening', IDONEO: 'Idoneo',
  ISCRITTO_AL_CORSO: 'Iscritto al corso', FREQUENTANTE: 'Frequentante',
  PRONTO_PER_PLACEMENT: 'Pronto per placement', PROPOSTO_AD_AZIENDA: 'Proposto ad azienda',
  COLLOQUIO_FISSATO: 'Colloquio fissato', SOMMINISTRATO: 'Somministrato', ASSUNTO: 'Assunto', SCARTATO: 'Scartato',
};

export function AddCourseCandidateModal({ onClose, courseId, stage }: { onClose: () => void; courseId: string; stage?: string }) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<'existing' | 'new'>('existing');
  const [candidateId, setCandidateId] = useState('');
  const [initialStage, setInitialStage] = useState<string>(stage ?? CandidateStage.NUOVO_CANDIDATO);
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '', city: '', skill: '', seniority: '' });

  const { data: candidates } = useQuery({
    queryKey: ['candidates', 'lite'],
    queryFn: () => api.get<{ items: Array<{ id: string; firstName: string; lastName: string; skill: string | null; seniority: string | null }> }>('/candidates?pageSize=200'),
  });

  const add = useMutation({
    mutationFn: () => api.post(`/courses/${courseId}/candidates`, mode === 'existing'
      ? { candidateId, stage: initialStage }
      : { ...form, email: form.email || undefined, stage: initialStage }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['course-pipeline', courseId] });
      qc.invalidateQueries({ queryKey: ['course', courseId] });
      qc.invalidateQueries({ queryKey: ['candidates'] });
      toast.ok('Candidato aggiunto al corso');
      onClose();
    },
    onError: (err) => toast.alert('Errore', err instanceof ApiError ? err.message : 'Riprova'),
  });

  const canSubmit = mode === 'existing' ? !!candidateId : !!form.firstName && !!form.lastName;

  return (
    <Modal
      title="Aggiungi al funnel del corso"
      kicker="Talent · Recruiting"
      onClose={onClose}
      size="lg"
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Annulla</button>
          <div className="spacer" />
          <button className="btn btn-primary" onClick={() => add.mutate()} disabled={!canSubmit || add.isPending}>
            Aggiungi <Icon name="arrowRight" size={12} />
          </button>
        </>
      }
    >
      <div className="form-section">
        <div className="form-grid">
          <Field label="Origine" span={8}>
            <div className="seg-control">
              <button aria-pressed={mode === 'existing'} onClick={() => setMode('existing')}>Dal talent pool</button>
              <button aria-pressed={mode === 'new'} onClick={() => setMode('new')}>Nuovo candidato</button>
            </div>
          </Field>
          <Field label="Colonna iniziale" span={4}>
            <select className="inp" value={initialStage} onChange={(e) => setInitialStage(e.target.value)}>
              {Object.values(CandidateStage).map((s) => <option key={s} value={s}>{STAGE_LABEL[s] ?? s}</option>)}
            </select>
          </Field>

          {mode === 'existing' ? (
            <Field label="Candidato" required span={12}>
              <select className="inp" value={candidateId} onChange={(e) => setCandidateId(e.target.value)}>
                <option value="">— Seleziona candidato —</option>
                {(candidates?.items ?? []).map((c) => (
                  <option key={c.id} value={c.id}>{c.firstName} {c.lastName}{c.skill ? ` · ${c.skill}` : ''}</option>
                ))}
              </select>
            </Field>
          ) : (
            <>
              <Field label="Nome" required span={6}>
                <input className="inp" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
              </Field>
              <Field label="Cognome" required span={6}>
                <input className="inp" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
              </Field>
              <Field label="Email" span={6}>
                <input className="inp" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </Field>
              <Field label="Telefono" span={6}>
                <input className="inp" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </Field>
              <Field label="Città" span={4}>
                <input className="inp" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
              </Field>
              <Field label="Competenza" span={4}>
                <input className="inp" value={form.skill} onChange={(e) => setForm({ ...form, skill: e.target.value })} />
              </Field>
              <Field label="Seniority" span={4}>
                <input className="inp" value={form.seniority} onChange={(e) => setForm({ ...form, seniority: e.target.value })} />
              </Field>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
